import type { NaviCondition } from '../config/condition.js';
import type { NaviFacts } from '../contract/facts.js';
import type { Platform } from '../contract/navData.js';

/** 各述語 kind がどの platform の facts を参照するか。and/or/not は再帰で導出する。 */
const LEAF_KIND_PLATFORM: Record<Exclude<NaviCondition['kind'], 'and' | 'or' | 'not'>, Platform> = {
  role: 'karte',
  option: 'karte',
  plugin: 'karte',
  i18nScope: 'karte',
  qantProductAdmin: 'qant',
  qantProductPii: 'qant',
  employee: 'qant',
  qantSmsEnabled: 'qant',
};

/**
 * condition ツリーが参照する platform の集合を返す。
 * `filterProducts` が「facts が欠けている platform を含む condition はツリーごとスキップ (= 表示)」
 * の判定に使う (platform 単位の all-or-nothing 後方互換)。
 */
export const collectConditionPlatforms = (condition: NaviCondition, into: Set<Platform> = new Set()): Set<Platform> => {
  switch (condition.kind) {
    case 'and':
    case 'or':
      for (const c of condition.conditions) collectConditionPlatforms(c, into);
      return into;
    case 'not':
      return collectConditionPlatforms(condition.condition, into);
    default:
      into.add(LEAF_KIND_PLATFORM[condition.kind]);
      return into;
  }
};

/**
 * メニューの `NaviCondition` を `NaviFacts` に対して評価する。
 * 述語の意味論は Karte 系は v5 (`navigation-v5`)、QANT 系は v1 ナビに忠実に合わせている
 * (`config/condition.ts` 参照)。
 *
 * 事実が部分的に欠落している場合の既定:
 * - `role`     : 対象 scope の付与が無ければ false (v5 `hasRole` 同様、権限なしは非表示)
 * - `option`   : 契約が無ければ false (option 未契約は非表示)
 * - `plugin`   : 未インストールは false
 * - `i18nScope`: `i18n` が無ければ `isGlobalEnterpriseScope=false` 扱い (= ja とみなし常時表示)
 * - `qantProductAdmin`: 対象 product が未掲載なら false (管理権限なしは非表示)
 * - `qantProductPii`: 対象 product が未掲載なら false (PII 権限なしは非表示)
 * - `employee` : isEmployee=false 扱い
 * - `qantSmsEnabled`: qantSmsEnabled が true でなければ false
 *
 * NOTE: 「platform の facts 自体が無い」ケースは呼び出し側 (`filterProducts`) が
 * `collectConditionPlatforms` を使って condition 評価をまるごとスキップすることで
 * 後方互換を担保する。本関数は参照する platform の facts が存在する前提で評価する。
 */
export const evaluateCondition = (condition: NaviCondition, facts: NaviFacts): boolean => {
  switch (condition.kind) {
    case 'role':
      return (facts.karte?.roles?.[condition.scope] ?? []).includes(condition.level);
    case 'option':
      return facts.karte?.options?.[condition.product]?.[condition.option] === true;
    case 'plugin':
      return facts.karte?.plugins?.[condition.plugin] === true;
    case 'i18nScope':
      // v5 i18nFilters: ja (= !isGlobalEnterpriseScope) なら scope 条件を無視して常に表示
      return !(facts.karte?.i18n?.isGlobalEnterpriseScope ?? false) || condition.requiredScope;
    case 'qantProductAdmin':
      return facts.qant?.productAdmin?.[condition.product] === true;
    case 'qantProductPii':
      return facts.qant?.productPii?.[condition.product] === true;
    case 'employee':
      return facts.qant?.isEmployee === true;
    case 'qantSmsEnabled':
      return facts.qant?.qantSmsEnabled === true;
    case 'and':
      return condition.conditions.every((c) => evaluateCondition(c, facts));
    case 'or':
      return condition.conditions.some((c) => evaluateCondition(c, facts));
    case 'not':
      return !evaluateCondition(condition.condition, facts);
  }
};
